import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Offer, OfferStatus } from "../../../stores/OfferStore/types";
import { List, ListItem, ListItemText } from "@mui/material";
import { Link } from "react-router-dom";
import useTranslation from "../../../hooks/useTranslation";
import "./OffersSummary.scss";
import OfferDialog from "../OfferDialog";
import { Case } from "../../../stores/CaseStore/types";
import caseStore from "../../../stores/CaseStore";
import { parseDate } from "../../../utils/helperFunctions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import OfferResultDialog from "../OfferResultDialog";
import configStore, { Flags } from "../../../stores/ConfigStore";

interface OffersSummaryProps {
  rawOffers: Offer[];
  incomingOffer?: boolean;
  summaryMode?: boolean;
}

const OffersSummary = observer(
  ({
    rawOffers,
    incomingOffer = false,
    summaryMode = false,
  }: OffersSummaryProps) => {
    const { t } = useTranslation();
    const [selectedOffer, setSelectedOffer] = useState<Offer>();
    const [caseItem, setCaseItem] = useState<Case>();
    const [openOfferDialog, setOpenOfferDialog] = useState(false);
    const [isCaseLoading, setIsCaseLoading] = useState(false);
    const [offers, setOffers] = useState<{
      newOffers: Offer[];
      pendingOffers: Offer[];
      approvedOffers: Offer[];
      rejectedOffers: Offer[];
      onHoldOffers: Offer[];
    }>({
      newOffers: [],
      pendingOffers: [],
      approvedOffers: [],
      rejectedOffers: [],
      onHoldOffers: [],
    });

    useEffect(() => {
      setOffers({
        newOffers: rawOffers.filter(
          (offer) => offer.status === OfferStatus.NEW
        ),
        pendingOffers: rawOffers.filter(
          (offer) => offer.status === OfferStatus.PENDING
        ),
        approvedOffers: rawOffers.filter(
          (offer) => offer.status === OfferStatus.APPROVED
        ),
        rejectedOffers: rawOffers.filter(
          (offer) => offer.status === OfferStatus.REJECTED
        ),
        onHoldOffers: rawOffers.filter(
          (offer) => offer.status === OfferStatus.ON_HOLD
        ),
      });
    }, [rawOffers]);

    useEffect(() => {
      if (!!selectedOffer) {
        setIsCaseLoading(true);
        caseStore
          .fetchCase(selectedOffer.caseId)
          .then((caseItem) => {
            setCaseItem(caseItem);
            setOpenOfferDialog(true);
          })
          .finally(() => setIsCaseLoading(false));
      }
    }, [selectedOffer]);

    const handleListItemClick = (offer: Offer) => {
      if (!isCaseLoading) {
        setSelectedOffer(offer);

        if (offer.id === selectedOffer?.id) {
          setOpenOfferDialog(true);
        }
      }
    };

    const summary = (
      <div className="summary">
        <span className="mb">
          <i className="fal fa-clock" /> {t("open-offers")}:&nbsp;
          {offers.pendingOffers.length}
        </span>
        <span className="mb">
          <i className="fal fa-lock" /> {t("on-hold-offers")}:&nbsp;
          {offers.onHoldOffers.length}
        </span>
        <span className="mb">
          <i className="fal fa-handshake" /> {t("approved-offers")}:&nbsp;
          {offers.approvedOffers.length}
        </span>
        <span className="mb">
          <i className="fal fa-vote-nay" /> {t("rejected-offers")}:&nbsp;
          {offers.rejectedOffers.length}
        </span>
      </div>
    );

    const offerItem = (offer: Offer) => (
      <ListItem key={offer.id} onClick={() => handleListItemClick(offer)}>
        <i className="fas fa-circle" />
        <ListItemText
          primary={
            <>
              <Typography variant={"body1"} fontWeight={"bold"}>
                {t("case-code")}: {offer.caseId}
              </Typography>
              <Typography
                variant={"body1"}
                fontSize={"16px"}
                fontWeight={"lighter"}
                sx={{ marginTop: "4px" }}
              >
                {offer.summary.substring(0, 80)}
                {offer.summary.length > 80 ? "..." : ""}
              </Typography>
            </>
          }
          secondary={
            <Box sx={{ marginTop: "8px" }}>
              {parseDate(offer.createDate || "")}
            </Box>
          }
        />
      </ListItem>
    );

    return summaryMode ? (
      summary
    ) : (
      <>
        {configStore.getFlag(Flags.isOfferApprovalRequired) && (
          <p>
            {offers.newOffers.length} {t("new-offer")}
          </p>
        )}
        <div className="offers">
          <div>
            <h3>
              <i className="fal fa-clock" /> {t("open-offers")} (
              {offers.pendingOffers.length})
            </h3>
            <List className="openOffers">
              {offers.pendingOffers.map((offer) => offerItem(offer))}
            </List>
          </div>
          <div>
            <h3>
              <i className="fal fa-lock" /> {t("on-hold-offers")} (
              {offers.onHoldOffers.length})
            </h3>
            <List className="onHoldOffers">
              {offers.onHoldOffers.map((offer) => offerItem(offer))}
            </List>
          </div>
          <div>
            <h3>
              <i className="fal fa-handshake" /> {t("approved-offers")} (
              {offers.approvedOffers.length})
            </h3>
            <List className="approvedOffers">
              {offers.approvedOffers.map((offer) => offerItem(offer))}
            </List>
          </div>
          <div>
            <h3>
              <i className="fal fa-vote-nay" /> {t("rejected-offers")} (
              {offers.rejectedOffers.length})
            </h3>
            <List className="rejectedOffers">
              {offers.rejectedOffers.map((offer) => offerItem(offer))}
            </List>
          </div>
        </div>
        <div className="seeMore">
          {(offers.pendingOffers.length > 3 ||
            offers.approvedOffers.length > 3 ||
            offers.rejectedOffers.length > 3) && (
            <Link to={"/offers"}>{t("see-more-offers")}</Link>
          )}
        </div>
        {caseItem && selectedOffer && openOfferDialog && incomingOffer && (
          <OfferResultDialog
            open={openOfferDialog}
            offers={rawOffers.filter((offer) => offer.caseId === caseItem.id)}
            setOpen={setOpenOfferDialog}
          />
        )}
        {caseItem && selectedOffer && openOfferDialog && !incomingOffer && (
          <OfferDialog
            readonly={true}
            caseItem={caseItem}
            offerId={selectedOffer.id}
            open={openOfferDialog}
            setOpen={setOpenOfferDialog}
          />
        )}
      </>
    );
  }
);

export default OffersSummary;
